import { exhaustiveGuard, isBoolean, isString, isStringLiteralOfType, type KeysOfValue } from "@mcwd/typescript-type-guards";
import { useFormLogger } from "./use-form-logger.js";
import
{
  type InjectionValuesRecord,
  type InjectionKeysRecord,
  CreateInjectionKeysRecord,
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  useRunWithContextFn
} from "@mcwd/util-vue";
import { type ComputedRef, computed, provide } from "vue";

type BaseCustomData = {
  formHeading?: string,
  secondFormHeading?: string,
  ctaButtonText?: string,
  CustomPostMessageHeader?: string,
  CustomPostMessageBody?: string
  hasPhoneValidation?: boolean,
  disableFormHeading?: boolean,
  multipleRowsLayout?: boolean,
  isLunchAndLearn?: boolean,
  eventType?: string,
};
type IntersectCustomData = BaseCustomData & { [key: string]: unknown };
export type CustomData = { [K in keyof IntersectCustomData]: IntersectCustomData[K] };
export type ComputedCustomData = { [K in keyof BaseCustomData]-?: ComputedRef<Required<IntersectCustomData>[K]> };

const CustomTextPossibleKeys = [
  "formHeading",
  "secondFormHeading",
  "ctaButtonText",
  "CustomPostMessageHeader",
  "CustomPostMessageBody",
  "eventType" ] as const satisfies KeysOfValue<Required<BaseCustomData>, string | null>[];
type CustomTextPossibleKeys = typeof CustomTextPossibleKeys[ number ];

const CustomBooleanPossibleKeys = [
  "disableFormHeading",
  "hasPhoneValidation",
  "multipleRowsLayout",
  "isLunchAndLearn" ] as const satisfies KeysOfValue<Required<BaseCustomData>, boolean>[];
type CustomBooleanPossibleKeys = typeof CustomBooleanPossibleKeys[ number ];

type AllCustomDataKeys = CustomTextPossibleKeys | CustomBooleanPossibleKeys;


type GetCustomFormTextFn = <T extends Record<CustomTextPossibleKeys | K, any> | undefined, K extends keyof any> (
  customData: T,
  customField: CustomTextPossibleKeys
) => (string | null);

type GetCustomFormBooleanFn = (
  customData: CustomData | undefined,
  customField: CustomBooleanPossibleKeys
) => boolean;

interface GetFormCustomDataValueFn
{
  (customData: CustomData | undefined, customField: CustomTextPossibleKeys): string | null;
  (customData: CustomData | undefined, customField: CustomBooleanPossibleKeys): boolean;
  (customData: CustomData | undefined, customField: AllCustomDataKeys): string | boolean | null;
}

type GetComputedPropertiesFn = (customData: CustomData | undefined) => ComputedCustomData;

type UseCustomDataInjectionValues = InjectionValuesRecord<{
  getCustomFormText: GetCustomFormTextFn,
  getCustomFormBoolean: GetCustomFormBooleanFn,
  getFormCustomDataValue: GetFormCustomDataValueFn,
  GetComputedProperties: GetComputedPropertiesFn
}>;

type UseCustomDataInjectionKeys = InjectionKeysRecord<UseCustomDataInjectionValues>;

const UseCustomDataInjectionKeys = CreateInjectionKeysRecord<UseCustomDataInjectionKeys>({
  getCustomFormText: "getCustomFormText",
  getCustomFormBoolean: "getCustomFormBoolean",
  getFormCustomDataValue: "getFormCustomDataValue",
  GetComputedProperties: "GetComputedProperties"
});

export function initCustomDataValueGetters()
{
  if (checkValuesAlreadyProvided(UseCustomDataInjectionKeys))
  {
    return;
  }
  const { getVerboseLogger } = useFormLogger({ callerLabel: 'use-custom-data-values.ts' });
  const getCustomFormText_verboseLogger = getVerboseLogger("getCustomFormText");
  const getCustomFormBoolean_verboseLogger = getVerboseLogger("getCustomFormBoolean");
  const getComputedProperties_verboseLogger = getVerboseLogger("GetComputedProperties");

  const getCustomFormTextFn: GetCustomFormTextFn = (
    customData,
    customField
  ) =>
  {
    const verboseLogger = getCustomFormText_verboseLogger;

    if (customData && (customField in customData)) {
      const customText: unknown = customData[ customField ];
      verboseLogger.debug(`Custom Form Text for field '${ customField }': `, customText);
      if (isString(customText) && customText.trim().length > 0) {
        return customText;
      }
    }
    return null;
  };


  const getCustomFormBooleanFn: GetCustomFormBooleanFn = (
    customData,
    customField
  ) =>
  {

    const verboseLogger = getCustomFormBoolean_verboseLogger;

    if (customData && (customField in customData))
    {
      const customValue: unknown = customData[ customField ];
      verboseLogger.debug(`Custom Form Value for field '${ customField }': `, customValue);
      if (isBoolean(customValue))
      {
        return customValue;
      }
    }
    return false;
  };


  //Generic function to retrieve both text and boolean 
  const getFormCustomDataValueFn: GetFormCustomDataValueFn = ((customData, customField, ) => {
    if (isStringLiteralOfType(customField, CustomTextPossibleKeys)) {
      return getCustomFormTextFn(customData, customField);
    }
    else if (isStringLiteralOfType(customField, CustomBooleanPossibleKeys)) {
      return getCustomFormBooleanFn(customData, customField);
    }
    else {
      exhaustiveGuard(customField);
    }
  }) as GetFormCustomDataValueFn;

  const GetComputedPropertiesFn: GetComputedPropertiesFn = (customData) =>
  {
    const ComputedCustomDataObj = {} as ComputedCustomData;

    const verboseLogger = getComputedProperties_verboseLogger;

    if (customData)
    {
      //string based properties
      for (const field of CustomTextPossibleKeys)
      {
        verboseLogger.debug(`Custom Form Text for field '${ field }': `, customData[ field ]);
        ComputedCustomDataObj[ field ] = computed(() => { return customData[ field ] ?? ""; });
      }
      //boolean based properties 
      for (const field of CustomBooleanPossibleKeys)
      {
        verboseLogger.debug(`Custom Form Boolean for field '${ field }': `, customData[ field ]);
        ComputedCustomDataObj[ field ] = computed(() => { return customData[ field ] ?? false; });
      }
    }
    return ComputedCustomDataObj;
  };
  provide(UseCustomDataInjectionKeys.getCustomFormText, getCustomFormTextFn);
  provide(UseCustomDataInjectionKeys.getCustomFormBoolean, getCustomFormBooleanFn);
  provide(UseCustomDataInjectionKeys.getFormCustomDataValue, getFormCustomDataValueFn);
  provide(UseCustomDataInjectionKeys.GetComputedProperties, GetComputedPropertiesFn);
}


export function useCustomDataValueGetters({ callerLabel }: { callerLabel?: string | undefined } = {})
{
  const { logger } = useFormLogger();
  try
  {
    callerLabel ??= "useCustomDataValueGetters";
    const getCustomFormTextFn = injectRequiredWithSelf(UseCustomDataInjectionKeys.getCustomFormText);
    const getCustomFormBooleanFn = injectRequiredWithSelf(UseCustomDataInjectionKeys.getCustomFormBoolean);
    const getFormCustomDataValueFn = injectRequiredWithSelf(UseCustomDataInjectionKeys.getFormCustomDataValue);
    const GetComputedPropertiesFn = injectRequiredWithSelf(UseCustomDataInjectionKeys.GetComputedProperties);

    return { 
      getCustomFormText: getCustomFormTextFn,
      getCustomFormBoolean: getCustomFormBooleanFn,
      getFormCustomDataValue: getFormCustomDataValueFn,
      GetComputedProperties: GetComputedPropertiesFn,
    };
  }
  catch (err)
  {
    logger.error(`InjectionError from ${ callerLabel ?? 'unknown' }`);
    throw err;
  }
}